import { createContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { HubConnection, HubConnectionState } from '@microsoft/signalr';
import { createSignalRConnection } from './signalRClient';
import { useAuth } from '@/auth/useAuth';

export interface SignalRContextType {
  connection: HubConnection | null;
  isConnected: boolean;
  connectionState: HubConnectionState;
  joinJobGroup: (jobId: string) => Promise<void>;
  leaveJobGroup: (jobId: string) => Promise<void>;
}

export const SignalRContext = createContext<SignalRContextType | null>(null);

interface SignalRProviderProps {
  children: ReactNode;
}

export const SignalRProvider = ({ children }: SignalRProviderProps) => {
  const { getAccessToken, user, isAuthenticated } = useAuth();
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<HubConnectionState>(
    HubConnectionState.Disconnected
  );

  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }

    const hubConnection = createSignalRConnection(getAccessToken);
    setConnection(hubConnection);

    const startConnection = async () => {
      try {
        await hubConnection.start();
        console.log('SignalR connected');
        setIsConnected(true);
        setConnectionState(HubConnectionState.Connected);

        // Join user-specific group
        const userId = user.profile.sub;
        if (userId) {
          await hubConnection.invoke('Connect', userId);
        }
      } catch (error) {
        console.error('SignalR connection error:', error);
        setIsConnected(false);
        setConnectionState(HubConnectionState.Disconnected);
      }
    };

    hubConnection.onreconnecting(() => {
      console.log('SignalR reconnecting...');
      setIsConnected(false);
      setConnectionState(HubConnectionState.Reconnecting);
    });

    hubConnection.onreconnected(() => {
      console.log('SignalR reconnected');
      setIsConnected(true);
      setConnectionState(HubConnectionState.Connected);

      // Rejoin user group
      const userId = user.profile.sub;
      if (userId) {
        hubConnection.invoke('Connect', userId).catch(console.error);
      }
    });

    hubConnection.onclose(() => {
      console.log('SignalR disconnected');
      setIsConnected(false);
      setConnectionState(HubConnectionState.Disconnected);
    });

    startConnection();

    return () => {
      if (hubConnection.state === HubConnectionState.Connected) {
        hubConnection.stop();
      }
    };
  }, [isAuthenticated, user, getAccessToken]);

  const joinJobGroup = useCallback(
    async (jobId: string) => {
      if (connection?.state === HubConnectionState.Connected) {
        try {
          await connection.invoke('ConnectJobGroup', jobId);
        } catch (error) {
          console.error('Failed to join job group:', error);
        }
      }
    },
    [connection]
  );

  const leaveJobGroup = useCallback(
    async (jobId: string) => {
      if (connection?.state === HubConnectionState.Connected) {
        try {
          await connection.invoke('DisconnectJobGroup', jobId);
        } catch (error) {
          console.error('Failed to leave job group:', error);
        }
      }
    },
    [connection]
  );

  const value: SignalRContextType = {
    connection,
    isConnected,
    connectionState,
    joinJobGroup,
    leaveJobGroup,
  };

  return <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>;
};
